/**
 * Client-side port of backend schedule-hash.utils.ts generateCrewScheduleHash.
 *
 * The backend stores this hash with every "Send Schedule to Crew" send
 * (comment metadata.sendResults[].scheduleHash). The dispatch page recomputes
 * the hash from the crew member's current schedule and compares — equal means
 * 'up-to-date', different means 'changed' (Ray's E.15: resend only to affected
 * movers, with the Changed badges meaning something).
 *
 * MUST stay in sync with backend/src/services/comments/comment-modes/schedule-hash.utils.ts
 * (same JSON.stringify input shape, same sha256 hex output).
 */
export interface ScheduleHashEvent {
    id: string;
    start: number;
    end: number;
    type?: string;
    locationIds?: string[];
    assetIds?: string[];
    attendeeIds?: string[];
    inventorySummary?: string;
}

/** Build the deterministic hash input (identical to the backend's). */
export function buildScheduleHashInput(
    crewMemberId: string,
    date: string,
    events: ScheduleHashEvent[],
): string {
    const sortedEvents = [...events].sort((a, b) => a.id.localeCompare(b.id));

    return JSON.stringify({
        crewMemberId,
        date,
        events: sortedEvents.map((e) => ({
            id: e.id,
            start: e.start,
            end: e.end,
            type: e.type || '',
            locationIds: [...(e.locationIds || [])].sort(),
            assetIds: [...(e.assetIds || [])].sort(),
            attendeeIds: [...(e.attendeeIds || [])].sort(),
            inventorySummary: e.inventorySummary || '',
        })),
    });
}

export function generateCrewScheduleHash(
    crewMemberId: string,
    date: string,
    events: ScheduleHashEvent[],
): Promise<string> {
    return sha256Hex(buildScheduleHashInput(crewMemberId, date, events));
}

// ─── sha256 with insecure-origin fallback ───────────────────────────────────
//
// `crypto-hash` uses crypto.subtle, which is undefined on insecure origins
// (http:// — e.g. the sisko dev host and other LAN deployments). The app's
// persisted-query link swallows that failure, but an explicit `await` here
// would throw and silently abort the whole send. Fall back to a compact
// pure-JS sha256 (public-domain shape, well-known constants) when subtle
// is unavailable.

async function sha256Hex(input: string): Promise<string> {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
        const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
        return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
    }
    return jsSha256(input);
}

const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

/** Compact pure-JS sha256 — identical output to crypto.subtle.digest('SHA-256'). */
function jsSha256(input: string): string {
    // UTF-8 encode
    const bytes: number[] = Array.from(new TextEncoder().encode(input));
    const bitLen = bytes.length * 8;

    // Padding: 0x80, zeros, then 64-bit big-endian length
    bytes.push(0x80);
    while (bytes.length % 64 !== 56) { bytes.push(0); }
    for (let i = 7; i >= 0; i--) {
        bytes.push(Math.floor(bitLen / Math.pow(2, i * 8)) & 0xff);
    }

    const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
    const w = new Array<number>(64);
    const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

    for (let chunk = 0; chunk < bytes.length; chunk += 64) {
        for (let t = 0; t < 16; t++) {
            w[t] = (bytes[chunk + t * 4] << 24) | (bytes[chunk + t * 4 + 1] << 16)
                | (bytes[chunk + t * 4 + 2] << 8) | bytes[chunk + t * 4 + 3];
        }
        for (let t = 16; t < 64; t++) {
            const s0 = rotr(w[t - 15], 7) ^ rotr(w[t - 15], 18) ^ (w[t - 15] >>> 3);
            const s1 = rotr(w[t - 2], 17) ^ rotr(w[t - 2], 19) ^ (w[t - 2] >>> 10);
            w[t] = (w[t - 16] + s0 + w[t - 7] + s1) | 0;
        }

        let a = H[0], b = H[1], c = H[2], d = H[3], e = H[4], f = H[5], g = H[6], h = H[7];
        for (let t = 0; t < 64; t++) {
            const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const temp1 = (h + S1 + ch + K[t] + w[t]) | 0;
            const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (S0 + maj) | 0;
            h = g; g = f; f = e;
            e = (d + temp1) | 0;
            d = c; c = b; b = a;
            a = (temp1 + temp2) | 0;
        }
        H[0] = (H[0] + a) | 0; H[1] = (H[1] + b) | 0; H[2] = (H[2] + c) | 0; H[3] = (H[3] + d) | 0;
        H[4] = (H[4] + e) | 0; H[5] = (H[5] + f) | 0; H[6] = (H[6] + g) | 0; H[7] = (H[7] + h) | 0;
    }

    return H.map((x) => (x >>> 0).toString(16).padStart(8, '0')).join('');
}

/**
 * Extract the most recent per-crew sendResults record from "Send Schedule to
 * Crew" thread comments (newest first). sendResults is a JSON-stringified
 * array of { userId, scheduleHash, sentAt, ... }.
 */
export function extractLastSentHashes(threads: any[]): Map<string, string> {
    const hashes = new Map<string, string>();
    if (!Array.isArray(threads)) return hashes;

    const sorted = [...threads].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    for (const thread of sorted) {
        const raw = thread?.metadata?.sendResults;
        if (!raw) continue;
        try {
            const results = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (!Array.isArray(results)) continue;
            for (const record of results) {
                if (record?.userId && record?.scheduleHash && record.status !== 'failed'
                    && !hashes.has(record.userId)) {
                    hashes.set(record.userId, record.scheduleHash);
                }
            }
        } catch {
            // skip malformed
        }
    }
    return hashes;
}